/**
 * AtcManifestSwrController - Stale-While-Revalidate orchestration
 *
 * Single Responsibility: Manages background refresh scheduling,
 * in-flight dedup, and subscriber notifications.  Has no knowledge of
 * how data is fetched or cached — it receives callbacks for those.
 */

import { Logger } from '@/app/utils/logger';
import { AtcStation } from '@/types/AtcManifest';
import { CircuitOpenError } from '@/app/services/resilience';

const LOG_TAG = 'atc-manifest-swr';

/**
 * Callback type for background refresh notifications
 */
export type BackgroundRefreshCallback = (stations: AtcStation[]) => void;

export class AtcManifestSwrController {
  // ── In-flight dedup ────────────────────────────────────────────

  private static mainManifestRefreshInFlight = false;
  private static stationManifestRefreshInFlight = new Set<string>();

  // ── Subscriber management ──────────────────────────────────────

  private static backgroundRefreshCallbacks: BackgroundRefreshCallback[] = [];

  /**
   * Register a callback to be notified when stations are refreshed in
   * the background.
   * @returns Unsubscribe function
   */
  static onBackgroundRefresh(callback: BackgroundRefreshCallback): () => void {
    this.backgroundRefreshCallbacks.push(callback);
    return () => {
      const index = this.backgroundRefreshCallbacks.indexOf(callback);
      if (index > -1) {
        this.backgroundRefreshCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Notify all registered callbacks about background refresh.
   */
  static notifyBackgroundRefresh(stations: AtcStation[]): void {
    this.backgroundRefreshCallbacks.forEach(callback => {
      try {
        callback(stations);
      } catch (error) {
        Logger.error(`Error in background refresh callback: ${error}`, LOG_TAG);
      }
    });
  }

  // ── In-flight status ───────────────────────────────────────────

  static isMainManifestRefreshInFlight(): boolean {
    return this.mainManifestRefreshInFlight;
  }

  static isStationManifestRefreshInFlight(manifestUrl: string): boolean {
    return this.stationManifestRefreshInFlight.has(manifestUrl);
  }

  // ── Background refresh scheduling ──────────────────────────────

  /**
   * Schedule a deferred background refresh for the main manifest.
   *
   * @param isCircuitOpen - Whether the circuit breaker is currently open
   * @param refreshFn - The async function that performs the actual refresh
   */
  static scheduleMainManifestRefresh(
    isCircuitOpen: boolean,
    refreshFn: () => Promise<void>,
  ): void {
    if (this.mainManifestRefreshInFlight) {
      Logger.debug('Main manifest background refresh already in flight, skipping', LOG_TAG);
      return;
    }

    // Defer to not block the caller (SWR pattern)
    setTimeout(() => {
      this.executeMainManifestRefresh(isCircuitOpen, refreshFn);
    }, 0);
  }

  /**
   * Schedule a deferred background refresh for a station manifest.
   */
  static scheduleStationManifestRefresh(
    manifestUrl: string,
    isCircuitOpen: boolean,
    refreshFn: () => Promise<void>,
  ): void {
    if (this.stationManifestRefreshInFlight.has(manifestUrl)) {
      Logger.debug(`Station manifest background refresh already in flight for ${manifestUrl}, skipping`, LOG_TAG);
      return;
    }

    setTimeout(() => {
      this.executeStationManifestRefresh(manifestUrl, isCircuitOpen, refreshFn);
    }, 0);
  }

  // ── Internal execution ─────────────────────────────────────────

  private static async executeMainManifestRefresh(
    isCircuitOpen: boolean,
    refreshFn: () => Promise<void>,
  ): Promise<void> {
    if (this.mainManifestRefreshInFlight) {
      Logger.debug('Main manifest background refresh already in flight, skipping', LOG_TAG);
      return;
    }

    if (isCircuitOpen) {
      Logger.info('Skipping main manifest background refresh - circuit is open', LOG_TAG);
      return;
    }

    this.mainManifestRefreshInFlight = true;
    Logger.info('Starting background refresh of main manifest', LOG_TAG);

    try {
      await refreshFn();
    } catch (error) {
      if (error instanceof CircuitOpenError) {
        Logger.info('Background refresh blocked by circuit breaker', LOG_TAG);
      } else {
        Logger.warn(`Background refresh failed: ${error}`, LOG_TAG);
      }
    } finally {
      this.mainManifestRefreshInFlight = false;
    }
  }

  private static async executeStationManifestRefresh(
    manifestUrl: string,
    isCircuitOpen: boolean,
    refreshFn: () => Promise<void>,
  ): Promise<void> {
    if (this.stationManifestRefreshInFlight.has(manifestUrl)) {
      Logger.debug(`Station manifest background refresh already in flight for ${manifestUrl}, skipping`, LOG_TAG);
      return;
    }

    if (isCircuitOpen) {
      Logger.info(`Skipping station manifest background refresh for ${manifestUrl} - circuit is open`, LOG_TAG);
      return;
    }

    this.stationManifestRefreshInFlight.add(manifestUrl);
    Logger.info(`Starting background refresh of station manifest: ${manifestUrl}`, LOG_TAG);

    try {
      await refreshFn();
      Logger.info(`Background refresh completed for station: ${manifestUrl}`, LOG_TAG);
    } catch (error) {
      if (error instanceof CircuitOpenError) {
        Logger.info(`Background refresh for ${manifestUrl} blocked by circuit breaker`, LOG_TAG);
      } else {
        Logger.warn(`Background refresh failed for ${manifestUrl}: ${error}`, LOG_TAG);
      }
    } finally {
      this.stationManifestRefreshInFlight.delete(manifestUrl);
    }
  }

  // ── Reset ──────────────────────────────────────────────────────

  /**
   * Reset all in-flight tracking (useful for testing).
   */
  static resetInFlightStatus(): void {
    this.mainManifestRefreshInFlight = false;
    this.stationManifestRefreshInFlight.clear();
  }

  /**
   * Clear everything (in-flight + subscribers).
   */
  static reset(): void {
    this.resetInFlightStatus();
    // Note: we intentionally do NOT clear callbacks here — the facade's
    // clearCache() resets in-flight only; subscribers persist until
    // explicitly unsubscribed.
  }
}
